'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useSyncExternalStore } from 'react';

import { APP_URL } from '../_lib/app-url';
import { useReveal } from './use-reveal';

function subscribeScroll(onChange: () => void) {
  window.addEventListener('scroll', onChange, { passive: true });
  return () => window.removeEventListener('scroll', onChange);
}

function getScrolledSnapshot(): boolean {
  return window.scrollY > 8;
}

function getScrolledServerSnapshot(): boolean {
  return false;
}

function useScrolled(): boolean {
  return useSyncExternalStore(subscribeScroll, getScrolledSnapshot, getScrolledServerSnapshot);
}

interface NavProps {
  ctaQuery?: string;
  /**
   * Brand link target: '' (homepage) keeps it a same-page `#` so campaign params survive
   * and Next's hash-only fast path avoids an RSC refetch; '/' (other pages) goes home.
   * Named to match Footer's prop, which still prefixes its `#how` anchor with it.
   */
  anchorPrefix?: '' | '/';
}

export function Nav({ ctaQuery = '', anchorPrefix = '' }: NavProps) {
  const scrolled = useScrolled();

  // Nav is always-mounted and always-client, so it's the natural home
  // for the page-level scroll-reveal observer. No separate wrapper.
  useReveal();

  return (
    <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
      <div className="nav-inner">
        <Link href={anchorPrefix ? '/' : '#'} className="nav-brand" aria-label="Loristry">
          <Image
            src="/loristry-logo-with-text.svg"
            alt="Loristry"
            width={120}
            height={28}
            className="nav-logo"
            style={{ width: 'auto', height: 28 }}
            priority
          />
        </Link>
        <div className="nav-links">
          <Link href="/book-samples">View a sample</Link>
          <Link href="/pricing">Pricing</Link>
          <Link href="/faq">FAQ</Link>
        </div>
        <div className="nav-right">
          <a href={`${APP_URL}/sign-in${ctaQuery}`} className="nav-signin">
            Sign in
          </a>
          <a href={`${APP_URL}/hello${ctaQuery}`} className="btn btn-primary btn-sm">
            Start a life story
          </a>
        </div>
      </div>
    </nav>
  );
}
